import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { Fogo3D } from '../componentes/FogoEFaiscasChoque3D'
import type { AparenciaRisco } from '../../dominio/tabletop/riscosDaCena'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

/**
 * 2. Bombeiro com Roupa de Combate a Incêndio / Salvamento
 *
 * Características solicitadas:
 * - Capa e calça em tecido ripstop aramida cáqui-dourado (#a16207 / #b45309)
 * - Faixas refletivas duplas de alta visibilidade (amarelo e prata)
 * - Capacete de bombeiro com casco vermelho e visor protetor
 * - Luvas de salvamento veicular de alta aderência
 * - Botas pretas de combate a incêndio
 * - SEM proteção respiratória autônoma (EPR/cilindro) nas costas
 */
export function BombeiroTabletop() {
  const corTecido = '#a16207'
  const corRefletivo = '#fef08a'
  const corPrata = '#f8fafc'
  const corBotas = '#0f172a'
  const corPele = '#e2b397'
  const corLuvas = '#ea580c'

  return (
    <group position={[0, 0, 0]}>
      {/* Botas pretas no solo */}
      {[-0.12, 0.12].map((x) => (
        <mesh key={x} position={[x, 0.07, 0.02]}>
          <CaixaArredondada args={[0.13, 0.14, 0.24]} />
          <meshStandardMaterial color={corBotas} roughness={0.8} />
        </mesh>
      ))}

      {/* Pernas com calça de proteção e faixas refletivas */}
      {[-0.12, 0.12].map((x) => (
        <group key={`perna-${x}`} position={[x, 0.48, 0]}>
          <mesh>
            <cylinderGeometry args={[0.11, 0.1, 0.72, 12]} />
            <meshStandardMaterial color={corTecido} roughness={0.65} />
          </mesh>
          {/* Faixa refletiva na canela */}
          <mesh position={[0, -0.18, 0]}>
            <cylinderGeometry args={[0.114, 0.114, 0.08, 12]} />
            <meshStandardMaterial color={corRefletivo} emissive={corRefletivo} emissiveIntensity={0.4} />
          </mesh>
          <mesh position={[0, -0.18, 0]}>
            <cylinderGeometry args={[0.116, 0.116, 0.03, 12]} />
            <meshStandardMaterial color={corPrata} />
          </mesh>
        </group>
      ))}

      {/* Pelve e Tronco com capa de aproximação */}
      <group position={[0, 1.15, 0]}>
        {/* Corpo da capa */}
        <mesh position={[0, 0, 0]}>
          <CaixaArredondada args={[0.46, 0.65, 0.28]} />
          <meshStandardMaterial color={corTecido} roughness={0.65} />
        </mesh>

        {/* Barra inferior da capa sobre o quadril */}
        <mesh position={[0, -0.32, 0]}>
          <CaixaArredondada args={[0.49, 0.14, 0.31]} />
          <meshStandardMaterial color={corTecido} roughness={0.65} />
        </mesh>
        <mesh position={[0, -0.32, 0]}>
          <CaixaArredondada args={[0.5, 0.05, 0.32]} />
          <meshStandardMaterial color={corRefletivo} emissive={corRefletivo} emissiveIntensity={0.4} />
        </mesh>

        {/* Faixa refletiva horizontal no peito e costas */}
        <mesh position={[0, 0.12, 0]}>
          <CaixaArredondada args={[0.47, 0.07, 0.29]} />
          <meshStandardMaterial color={corRefletivo} emissive={corRefletivo} emissiveIntensity={0.4} />
        </mesh>
        <mesh position={[0, 0.12, 0]}>
          <CaixaArredondada args={[0.472, 0.03, 0.292]} />
          <meshStandardMaterial color={corPrata} />
        </mesh>

        {/* Ombros reforçados */}
        <mesh position={[0, 0.26, 0]}>
          <CaixaArredondada args={[0.56, 0.1, 0.3]} />
          <meshStandardMaterial color={corTecido} roughness={0.65} />
        </mesh>

        {/* Inscrição refletiva nas costas (SEM cilindro nas costas) */}
        <mesh position={[0, 0.12, -0.146]}>
          <planeGeometry args={[0.32, 0.08]} />
          <meshStandardMaterial color={corRefletivo} emissive={corRefletivo} emissiveIntensity={0.6} side={2} />
        </mesh>

        {/* Braços com faixas nos punhos e luvas de resgate */}
        {[-1, 1].map((lado) => (
          <group key={`braco-${lado}`} position={[lado * 0.32, 0.2, 0]} rotation={[0.15, 0, lado * 0.1]}>
            <mesh position={[0, -0.22, 0]}>
              <cylinderGeometry args={[0.075, 0.07, 0.44, 10]} />
              <meshStandardMaterial color={corTecido} roughness={0.65} />
            </mesh>
            {/* Faixa refletiva no punho */}
            <mesh position={[0, -0.32, 0]}>
              <cylinderGeometry args={[0.078, 0.078, 0.06, 10]} />
              <meshStandardMaterial color={corRefletivo} emissive={corRefletivo} emissiveIntensity={0.4} />
            </mesh>
            {/* Luva de salvamento veicular */}
            <mesh position={[0, -0.46, 0.02]}>
              <CaixaArredondada args={[0.1, 0.12, 0.11]} />
              <meshStandardMaterial color={corLuvas} roughness={0.7} />
            </mesh>
          </group>
        ))}

        {/* Pescoço e Cabeça */}
        <mesh position={[0, 0.36, 0]}>
          <cylinderGeometry args={[0.07, 0.08, 0.12, 10]} />
          <meshStandardMaterial color={corPele} roughness={0.6} />
        </mesh>
        <mesh position={[0, 0.48, 0]}>
          <sphereGeometry args={[0.12, 16, 14]} />
          <meshStandardMaterial color={corPele} roughness={0.6} />
        </mesh>

        {/* Capacete de Bombeiro com crista e visor */}
        <group position={[0, 0.54, 0]}>
          {/* Casco rígido vermelho */}
          <mesh position={[0, 0.03, 0]}>
            <sphereGeometry args={[0.145, 18, 14]} />
            <meshStandardMaterial color="#dc2626" roughness={0.3} />
          </mesh>
          {/* Aba do capacete */}
          <mesh position={[0, -0.02, 0]}>
            <cylinderGeometry args={[0.175, 0.185, 0.03, 18]} />
            <meshStandardMaterial color="#b91c1c" roughness={0.4} />
          </mesh>
          {/* Crista superior */}
          <mesh position={[0, 0.14, 0]}>
            <CaixaArredondada args={[0.04, 0.07, 0.22]} />
            <meshStandardMaterial color="#fef08a" roughness={0.4} />
          </mesh>
          {/* Visor dourado protetor */}
          <mesh position={[0, 0.01, 0.13]}>
            <CaixaArredondada args={[0.18, 0.09, 0.04]} />
            <meshStandardMaterial color="#ca8a04" metalness={0.7} roughness={0.15} />
          </mesh>
        </group>
      </group>
    </group>
  )
}

/**
 * 3. Bombeiro Médico / Resgatista de APH
 *
 * Características solicitadas:
 * - Roupa de bombeiro (capa de incêndio/resgate) com faixas refletivas
 * - Capacete de bombeiro branco (padrão de comando e saúde) com cruz médica
 * - Mão direita: Bolsa de primeiros socorros vermelha com cruz branca (Kit Trauma)
 * - Mão esquerda: Cilindro portátil de Oxigênio (O2) medicinal verde com manômetro
 */
export function BombeiroMedicoTabletop() {
  const corTecido = '#a16207'
  const corRefletivo = '#fef08a'
  const corPrata = '#f8fafc'
  const corBotas = '#0f172a'
  const corPele = '#e2b397'

  return (
    <group position={[0, 0, 0]}>
      {/* Botas pretas */}
      {[-0.12, 0.12].map((x) => (
        <mesh key={x} position={[x, 0.07, 0.02]}>
          <CaixaArredondada args={[0.13, 0.14, 0.24]} />
          <meshStandardMaterial color={corBotas} roughness={0.8} />
        </mesh>
      ))}

      {/* Pernas com calça de proteção */}
      {[-0.12, 0.12].map((x) => (
        <group key={`perna-${x}`} position={[x, 0.48, 0]}>
          <mesh>
            <cylinderGeometry args={[0.11, 0.1, 0.72, 12]} />
            <meshStandardMaterial color={corTecido} roughness={0.65} />
          </mesh>
          <mesh position={[0, -0.18, 0]}>
            <cylinderGeometry args={[0.114, 0.114, 0.08, 12]} />
            <meshStandardMaterial color={corRefletivo} emissive={corRefletivo} emissiveIntensity={0.4} />
          </mesh>
        </group>
      ))}

      {/* Pelve e Tronco com capa */}
      <group position={[0, 1.15, 0]}>
        <mesh position={[0, 0, 0]}>
          <CaixaArredondada args={[0.46, 0.65, 0.28]} />
          <meshStandardMaterial color={corTecido} roughness={0.65} />
        </mesh>
        <mesh position={[0, 0.12, 0]}>
          <CaixaArredondada args={[0.47, 0.07, 0.29]} />
          <meshStandardMaterial color={corRefletivo} emissive={corRefletivo} emissiveIntensity={0.4} />
        </mesh>
        <mesh position={[0, 0.12, 0]}>
          <CaixaArredondada args={[0.472, 0.03, 0.292]} />
          <meshStandardMaterial color={corPrata} />
        </mesh>

        {/* Tarja médica nas costas e tórax: "RESGATE / MÉDICO" */}
        <mesh position={[0, 0.12, -0.146]}>
          <planeGeometry args={[0.34, 0.08]} />
          <meshStandardMaterial color="#0284c7" emissive="#0284c7" emissiveIntensity={0.4} side={2} />
        </mesh>

        {/* Cabeça e Capacete Branco de Saúde/Comando */}
        <mesh position={[0, 0.36, 0]}>
          <cylinderGeometry args={[0.07, 0.08, 0.12, 10]} />
          <meshStandardMaterial color={corPele} />
        </mesh>
        <mesh position={[0, 0.48, 0]}>
          <sphereGeometry args={[0.12, 16, 14]} />
          <meshStandardMaterial color={corPele} />
        </mesh>

        <group position={[0, 0.54, 0]}>
          <mesh position={[0, 0.03, 0]}>
            <sphereGeometry args={[0.145, 18, 14]} />
            <meshStandardMaterial color="#f8fafc" roughness={0.3} />
          </mesh>
          <mesh position={[0, -0.02, 0]}>
            <cylinderGeometry args={[0.175, 0.185, 0.03, 18]} />
            <meshStandardMaterial color="#e2e8f0" roughness={0.4} />
          </mesh>
          {/* Cruz médica na frente do capacete */}
          <group position={[0, 0.05, 0.14]}>
            <mesh>
              <CaixaArredondada args={[0.025, 0.07, 0.01]} />
              <meshStandardMaterial color="#ef4444" />
            </mesh>
            <mesh>
              <CaixaArredondada args={[0.07, 0.025, 0.01]} />
              <meshStandardMaterial color="#ef4444" />
            </mesh>
          </group>
        </group>

        {/* --- BRAÇO DIREITO SEGURANDO A BOLSA DE PRIMEIROS SOCORROS VERMELHA --- */}
        <group position={[-0.32, 0.2, 0]} rotation={[0.2, 0, -0.15]}>
          <mesh position={[0, -0.22, 0]}>
            <cylinderGeometry args={[0.075, 0.07, 0.44, 10]} />
            <meshStandardMaterial color={corTecido} roughness={0.65} />
          </mesh>
          {/* Mão segurando a alça */}
          <mesh position={[0, -0.46, 0.02]}>
            <CaixaArredondada args={[0.09, 0.1, 0.1]} />
            <meshStandardMaterial color={corPele} />
          </mesh>

          {/* Bolsa de Primeiros Socorros Vermelha com Cruz Branca */}
          <group position={[-0.08, -0.68, 0.06]}>
            {/* Corpo da bolsa */}
            <mesh>
              <CaixaArredondada args={[0.26, 0.32, 0.46]} />
              <meshStandardMaterial color="#dc2626" roughness={0.7} />
            </mesh>
            {/* Faixa refletiva lateral */}
            <mesh position={[0, 0, 0]}>
              <CaixaArredondada args={[0.264, 0.05, 0.464]} />
              <meshStandardMaterial color="#f8fafc" />
            </mesh>
            {/* Cruz Branca de Primeiros Socorros em ambos os lados */}
            {[-1, 1].map((lado) => (
              <group key={lado} position={[lado * 0.134, 0, 0]} rotation={[0, lado * Math.PI / 2, 0]}>
                <mesh>
                  <planeGeometry args={[0.045, 0.16]} />
                  <meshBasicMaterial color="#ffffff" side={2} />
                </mesh>
                <mesh>
                  <planeGeometry args={[0.16, 0.045]} />
                  <meshBasicMaterial color="#ffffff" side={2} />
                </mesh>
              </group>
            ))}
            {/* Alça superior */}
            <mesh position={[0, 0.18, 0]}>
              <CaixaArredondada args={[0.04, 0.08, 0.22]} />
              <meshStandardMaterial color="#1e293b" />
            </mesh>
          </group>
        </group>

        {/* --- BRAÇO ESQUERDO SEGURANDO O CILINDRO DE OXIGÊNIO (O2) VERDE --- */}
        <group position={[0.32, 0.2, 0]} rotation={[0.2, 0, 0.15]}>
          <mesh position={[0, -0.22, 0]}>
            <cylinderGeometry args={[0.075, 0.07, 0.44, 10]} />
            <meshStandardMaterial color={corTecido} roughness={0.65} />
          </mesh>
          {/* Mão segurando a alça do cilindro */}
          <mesh position={[0, -0.46, 0.02]}>
            <CaixaArredondada args={[0.09, 0.1, 0.1]} />
            <meshStandardMaterial color={corPele} />
          </mesh>

          {/* Cilindro Portátil de O2 Medicinal Verde */}
          <group position={[0.06, -0.66, 0.04]}>
            {/* Corpo cilíndrico verde medicinal */}
            <mesh position={[0, 0, 0]}>
              <cylinderGeometry args={[0.08, 0.08, 0.46, 16]} />
              <meshStandardMaterial color="#15803d" metalness={0.4} roughness={0.4} />
            </mesh>
            {/* Cúpula ogival superior branca/verde */}
            <mesh position={[0, 0.24, 0]}>
              <sphereGeometry args={[0.08, 16, 12]} />
              <meshStandardMaterial color="#f8fafc" roughness={0.3} />
            </mesh>
            {/* Regulador e manômetro de pressão cromado */}
            <mesh position={[0, 0.35, 0]}>
              <CaixaArredondada args={[0.06, 0.1, 0.06]} />
              <meshStandardMaterial color="#cbd5e1" metalness={0.8} />
            </mesh>
            {/* Manômetro circular */}
            <mesh position={[0.04, 0.36, 0]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.035, 0.035, 0.02, 12]} />
              <meshStandardMaterial color="#f8fafc" />
            </mesh>
            {/* Mangueira de oxigênio espiralada */}
            <mesh position={[0, 0.08, 0.08]}>
              <torusGeometry args={[0.06, 0.015, 8, 16]} />
              <meshStandardMaterial color="#22c55e" />
            </mesh>
          </group>
        </group>
      </group>
    </group>
  )
}

/**
 * 4. Espaço Livre para a USA (Unidade de Suporte Avançado / Ambulância Médica)
 *
 * Plano tático demarcado no asfalto:
 * - Área de 3.6m x 8.0m (dimensão segura para manobra da ambulância)
 * - Bordas tracejadas de segurança em amarelo e branco
 * - Símbolo de primeiros socorros / Estrela da Vida
 * - Identificação operacional: "ÁREA LIVRE — CHEGADA USA (SUPORTE AVANÇADO)"
 */
export function AreaUsaTabletop() {
  const noRaycast = () => null

  return (
    <group position={[0, 0.003, 0]}>
      {/* Área demarcada no solo com leve opacidade */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
        <planeGeometry args={[3.6, 7.8]} />
        <meshBasicMaterial color="#0284c7" transparent opacity={0.12} depthWrite={false} side={2} />
      </mesh>

      {/* Borda tracejada perimetral */}
      {/* Linhas longitudinais */}
      {[-1.8, 1.8].map((x) => (
        <mesh key={x} position={[x, 0.001, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
          <planeGeometry args={[0.1, 7.8]} />
          <meshBasicMaterial color="#facc15" transparent opacity={0.85} depthWrite={false} side={2} />
        </mesh>
      ))}

      {/* Linhas transversais */}
      {[-3.9, 3.9].map((z) => (
        <mesh key={z} position={[0, 0.001, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
          <planeGeometry args={[3.6, 0.1]} />
          <meshBasicMaterial color="#facc15" transparent opacity={0.85} depthWrite={false} side={2} />
        </mesh>
      ))}

      {/* Cruz Médica / Estrela no centro */}
      <group position={[0, 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <mesh raycast={noRaycast}>
          <planeGeometry args={[0.4, 1.4]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.9} depthWrite={false} side={2} />
        </mesh>
        <mesh raycast={noRaycast}>
          <planeGeometry args={[1.4, 0.4]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.9} depthWrite={false} side={2} />
        </mesh>
      </group>
    </group>
  )
}

/**
 * 5. Mancha de Sangue no Solo / Veículo (Risco Biológico e Hemorragia Externa)
 *
 * Simula uma poça líquida orgânica de sangue coagulado:
 * - Geometria de múltiplos lóbulos orgânicos concêntricos e excêntricos
 * - Vermelho profundo coagulado (#3b0202, #4a0404 e #7f1d1d)
 * - Brilho úmido especular (roughness 0.06, metalness 0.2)
 * - Gotas satélites de respingo ao redor do contorno
 */
export function ManchaSangueTabletop({
  aparencia,
}: {
  aparencia?: { areaIsolada?: boolean }
}) {
  const noRaycast = () => null

  return (
    <group position={[0, 0.012, 0]}>
      {/* Poça central principal espessa */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} scale={[1, 0.78, 1]} raycast={noRaycast}>
        <circleGeometry args={[0.55, 48]} />
        <meshStandardMaterial color="#3b0202" roughness={0.06} metalness={0.2} />
      </mesh>

      {/* Lóbulo orgânico secundário esquerdo */}
      <mesh position={[-0.22, 0.001, 0.15]} rotation={[-Math.PI / 2, 0, 0.4]} scale={[0.42, 0.32, 1]} raycast={noRaycast}>
        <circleGeometry args={[0.5, 32]} />
        <meshStandardMaterial color="#4a0404" roughness={0.07} metalness={0.15} />
      </mesh>

      {/* Lóbulo orgânico secundário direito */}
      <mesh position={[0.26, 0.001, -0.12]} rotation={[-Math.PI / 2, 0, -0.6]} scale={[0.48, 0.36, 1]} raycast={noRaycast}>
        <circleGeometry args={[0.5, 32]} />
        <meshStandardMaterial color="#4a0404" roughness={0.07} metalness={0.15} />
      </mesh>

      {/* Camada líquida translúcida carmesim superior (brilho de fluido fresco nas bordas) */}
      <mesh position={[0, 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1.05, 0.82, 1]} raycast={noRaycast}>
        <ringGeometry args={[0.3, 0.58, 48]} />
        <meshStandardMaterial
          color="#7f1d1d"
          emissive="#450a0a"
          emissiveIntensity={0.25}
          roughness={0.08}
          metalness={0.2}
          transparent
          opacity={0.88}
          depthWrite={false}
        />
      </mesh>

      {/* Gotas e respingos satélites em volta da poça */}
      {[
        [-0.58, 0.28, 0.06],
        [-0.45, -0.38, 0.045],
        [0.52, 0.32, 0.05],
        [0.64, -0.18, 0.055],
        [0.12, 0.52, 0.04],
        [-0.18, -0.48, 0.04],
        [0.35, -0.42, 0.035],
      ].map(([x, z, r], i) => (
        <mesh key={i} position={[x, 0.001, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
          <circleGeometry args={[r, 16]} />
          <meshStandardMaterial color="#450a0a" roughness={0.06} metalness={0.1} />
        </mesh>
      ))}

      {aparencia?.areaIsolada && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.003, 0]}>
          <ringGeometry args={[1.0, 1.06, 32]} />
          <meshBasicMaterial color="#facc15" side={2} />
        </mesh>
      )}

      {/* Área interativa de toque */}
      <mesh visible={false} position={[0, 0.1, 0]}>
        <boxGeometry args={[1.3, 0.2, 1.1]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

/**
 * 6. Extintor de Incêndio PQS (Pó Químico Seco 4kg/6kg)
 *
 * Equipamento indispensável de segurança e linha de prontidão em salvamento veicular:
 * - Cilindro vermelho bombeiro (#dc2626) com base antiderrapante em polímero escuro
 * - Cabeçote de válvula metálico em latão/aço escuro com manômetro indicador
 * - Gatilho de acionamento e alça de transporte
 * - Mangueira de descarga flexível de borracha com bico difusor cônico
 * - Faixa de instruções de operação em branco
 */
export function ExtintorPqsTabletop() {
  return (
    <group position={[0, 0.28, 0]}>
      {/* Base / saia protetora do fundo */}
      <mesh position={[0, -0.25, 0]}>
        <cylinderGeometry args={[0.092, 0.095, 0.06, 24]} />
        <meshStandardMaterial color="#0f172a" roughness={0.8} />
      </mesh>

      {/* Cilindro principal pressurizado em vermelho vivo */}
      <mesh position={[0, -0.04, 0]}>
        <cylinderGeometry args={[0.09, 0.09, 0.42, 24]} />
        <meshStandardMaterial color="#dc2626" roughness={0.32} metalness={0.15} />
      </mesh>

      {/* Cúpula superior do cilindro */}
      <mesh position={[0, 0.17, 0]}>
        <sphereGeometry args={[0.09, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#dc2626" roughness={0.32} metalness={0.15} />
      </mesh>

      {/* Rótulo informativo no corpo do cilindro */}
      <mesh position={[0, -0.04, 0]}>
        <cylinderGeometry args={[0.091, 0.091, 0.16, 24]} />
        <meshStandardMaterial color="#f8fafc" roughness={0.5} />
      </mesh>

      {/* Gargalo e válvula em latão/aço */}
      <mesh position={[0, 0.23, 0]}>
        <cylinderGeometry args={[0.024, 0.03, 0.06, 16]} />
        <meshStandardMaterial color="#475569" metalness={0.8} roughness={0.3} />
      </mesh>

      {/* Alça de transporte fixa */}
      <mesh position={[-0.04, 0.26, 0]} rotation={[0, 0, -0.2]}>
        <CaixaArredondada args={[0.1, 0.015, 0.025]} />
        <meshStandardMaterial color="#1e293b" metalness={0.7} />
      </mesh>

      {/* Gatilho móvel superior de descarga */}
      <mesh position={[-0.04, 0.29, 0]} rotation={[0, 0, 0.15]}>
        <CaixaArredondada args={[0.11, 0.012, 0.022]} />
        <meshStandardMaterial color="#1e293b" metalness={0.7} />
      </mesh>

      {/* Manômetro diminuto de pressão */}
      <mesh position={[0.035, 0.245, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.018, 0.018, 0.015, 16]} />
        <meshStandardMaterial color="#e2e8f0" metalness={0.3} roughness={0.3} />
      </mesh>

      {/* Mangueira de descarga lateral */}
      <mesh position={[0.085, 0.04, 0]}>
        <cylinderGeometry args={[0.01, 0.01, 0.36, 12]} />
        <meshStandardMaterial color="#0f172a" roughness={0.9} />
      </mesh>

      {/* Bico difusor cônico no final da mangueira */}
      <mesh position={[0.085, -0.16, 0]} rotation={[Math.PI, 0, 0]}>
        <cylinderGeometry args={[0.018, 0.008, 0.06, 16]} />
        <meshStandardMaterial color="#0f172a" roughness={0.8} />
      </mesh>

      {/* Área de toque */}
      <mesh visible={false}>
        <boxGeometry args={[0.3, 0.65, 0.3]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

/**
 * 7. Mochila de Trauma e Resgate APH (Atendimento Pré-Hospitalar)
 *
 * Utilizada pela equipe de resgate / socorristas médicos:
 * - Corpo em lona de cordura vermelha de alta resistência (#b91c1c)
 * - Bolsos frontais e laterais modulares com zíperes
 * - Cruz da Vida em branco centralizada
 * - Faixas refletivas amarelas de alta visibilidade
 * - Alça de transporte superior robusta
 */
export function MochilaTraumaTabletop() {
  return (
    <group position={[0, 0.21, 0]}>
      {/* Corpo principal da mochila */}
      <mesh position={[0, 0, 0]}>
        <CaixaArredondada args={[0.34, 0.42, 0.24]} />
        <meshStandardMaterial color="#b91c1c" roughness={0.65} />
      </mesh>

      {/* Faixas refletivas amarelas horizontais */}
      {[-0.08, 0.12].map((y) => (
        <mesh key={y} position={[0, y, 0.122]}>
          <planeGeometry args={[0.32, 0.03]} />
          <meshStandardMaterial color="#facc15" emissive="#facc15" emissiveIntensity={0.2} roughness={0.3} />
        </mesh>
      ))}

      {/* Compartimento frontal protuberante */}
      <mesh position={[0, 0.02, 0.14]}>
        <CaixaArredondada args={[0.26, 0.28, 0.06]} />
        <meshStandardMaterial color="#991b1b" roughness={0.7} />
      </mesh>

      {/* Cruz de Emergência branca no bolso frontal */}
      <group position={[0, 0.02, 0.172]}>
        <mesh>
          <planeGeometry args={[0.04, 0.14]} />
          <meshBasicMaterial color="#ffffff" side={2} />
        </mesh>
        <mesh>
          <planeGeometry args={[0.14, 0.04]} />
          <meshBasicMaterial color="#ffffff" side={2} />
        </mesh>
      </group>

      {/* Bolsos laterais para kits rápidos de gaze/torniquete */}
      {[-1, 1].map((lado) => (
        <mesh key={lado} position={[lado * 0.185, -0.03, 0]}>
          <CaixaArredondada args={[0.04, 0.26, 0.18]} />
          <meshStandardMaterial color="#991b1b" roughness={0.7} />
        </mesh>
      ))}

      {/* Alça superior reforçada de transporte manual */}
      <mesh position={[0, 0.23, 0]}>
        <CaixaArredondada args={[0.12, 0.04, 0.03]} />
        <meshStandardMaterial color="#1e293b" roughness={0.8} />
      </mesh>

      {/* Fundo emborrachado preto reforçado para apoio no solo úmido/asfalto */}
      <mesh position={[0, -0.21, 0]}>
        <CaixaArredondada args={[0.35, 0.02, 0.25]} />
        <meshStandardMaterial color="#0f172a" roughness={0.9} />
      </mesh>

      {/* Área de toque */}
      <mesh visible={false}>
        <boxGeometry args={[0.42, 0.5, 0.35]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

/**
 * 8. Lona de Ferramentas — Tool Staging Area (Resgate Veicular Moderno)
 *
 * Conforme a doutrina operacional moderna:
 * - Lona vinílica retangular em Laranja Resgate (#ea580c) estendida no solo (2.4m x 1.6m)
 * - Moldura perimetral preta reforçada com ilhoses
 * - Equipamentos modernos a bateria (sem motobomba a combustão nem mangueiras pesadas):
 *   1. Cortador / Desencarcerador a bateria (bloco ergonômico + bateria + lâminas de corte curvas)
 *   2. Alargador / Expansor a bateria (braços em V abertos com pontas dentadas)
 *   3. Conjunto de estabilização (calços escalonados de madeira e bloco de cribbing)
 *   4. Prancha longa rígida amarela de resgate com imobilizadores
 */
export function LonaFerramentasTabletop() {
  const noRaycast = () => null

  return (
    <group position={[0, 0.004, 0]}>
      {/* Lona vinílica principal no solo em Laranja Resgate */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
        <planeGeometry args={[2.4, 1.6]} />
        <meshStandardMaterial color="#ea580c" roughness={0.65} metalness={0.05} side={2} />
      </mesh>

      {/* Moldura / borda reforçada preta da lona */}
      {[-1.2, 1.2].map((x) => (
        <mesh key={x} position={[x, 0.001, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
          <planeGeometry args={[0.06, 1.6]} />
          <meshBasicMaterial color="#1e293b" side={2} />
        </mesh>
      ))}
      {[-0.8, 0.8].map((z) => (
        <mesh key={z} position={[0, 0.001, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast}>
          <planeGeometry args={[2.4, 0.06]} />
          <meshBasicMaterial color="#1e293b" side={2} />
        </mesh>
      ))}

      {/* ITEM 1: Cortador / Tesoura a Bateria (lado esquerdo) */}
      <group position={[-0.65, 0.09, -0.32]} rotation={[0, 0.25, 0]}>
        <mesh position={[0, 0, 0]}>
          <CaixaArredondada args={[0.38, 0.14, 0.14]} />
          <meshStandardMaterial color="#dc2626" roughness={0.4} />
        </mesh>
        <mesh position={[-0.24, 0.01, 0]}>
          <CaixaArredondada args={[0.12, 0.11, 0.12]} />
          <meshStandardMaterial color="#0f172a" roughness={0.8} />
        </mesh>
        <mesh position={[0.05, 0.1, 0]}>
          <CaixaArredondada args={[0.16, 0.06, 0.18]} />
          <meshStandardMaterial color="#1e293b" metalness={0.7} />
        </mesh>
        <mesh position={[0.26, 0.01, -0.03]} rotation={[0, 0.2, 0]}>
          <CaixaArredondada args={[0.18, 0.03, 0.05]} />
          <meshStandardMaterial color="#cbd5e1" metalness={0.9} roughness={0.2} />
        </mesh>
        <mesh position={[0.26, 0.01, 0.03]} rotation={[0, -0.2, 0]}>
          <CaixaArredondada args={[0.18, 0.03, 0.05]} />
          <meshStandardMaterial color="#cbd5e1" metalness={0.9} roughness={0.2} />
        </mesh>
      </group>

      {/* ITEM 2: Alargador / Expansor a Bateria (centro-esquerda) */}
      <group position={[-0.55, 0.09, 0.35]} rotation={[0, -0.15, 0]}>
        <mesh position={[0, 0, 0]}>
          <CaixaArredondada args={[0.42, 0.15, 0.15]} />
          <meshStandardMaterial color="#dc2626" roughness={0.4} />
        </mesh>
        <mesh position={[-0.26, 0.01, 0]}>
          <CaixaArredondada args={[0.12, 0.12, 0.13]} />
          <meshStandardMaterial color="#0f172a" roughness={0.8} />
        </mesh>
        <mesh position={[0.28, 0.01, -0.09]} rotation={[0, 0.35, 0]}>
          <CaixaArredondada args={[0.22, 0.04, 0.06]} />
          <meshStandardMaterial color="#cbd5e1" metalness={0.9} roughness={0.2} />
        </mesh>
        <mesh position={[0.28, 0.01, 0.09]} rotation={[0, -0.35, 0]}>
          <CaixaArredondada args={[0.22, 0.04, 0.06]} />
          <meshStandardMaterial color="#cbd5e1" metalness={0.9} roughness={0.2} />
        </mesh>
      </group>

      {/* ITEM 3: Equipamentos de Estabilização (Calços e Blocos) */}
      <group position={[0.22, 0.06, -0.35]}>
        <mesh position={[0, 0.04, 0]}>
          <CaixaArredondada args={[0.42, 0.08, 0.14]} />
          <meshStandardMaterial color="#b45309" roughness={0.85} />
        </mesh>
        <mesh position={[0.06, 0.1, 0]}>
          <CaixaArredondada args={[0.28, 0.06, 0.14]} />
          <meshStandardMaterial color="#b45309" roughness={0.85} />
        </mesh>
        <mesh position={[0.12, 0.15, 0]}>
          <CaixaArredondada args={[0.14, 0.05, 0.14]} />
          <meshStandardMaterial color="#b45309" roughness={0.85} />
        </mesh>
        <mesh position={[0, 0.05, 0.22]}>
          <CaixaArredondada args={[0.35, 0.09, 0.1]} />
          <meshStandardMaterial color="#92400e" roughness={0.9} />
        </mesh>
        <mesh position={[0, 0.14, 0.22]} rotation={[0, Math.PI / 2, 0]}>
          <CaixaArredondada args={[0.35, 0.09, 0.1]} />
          <meshStandardMaterial color="#92400e" roughness={0.9} />
        </mesh>
      </group>

      {/* ITEM 4: Prancha Longa Rígida de Coluna */}
      <group position={[0.78, 0.04, 0]}>
        <mesh position={[0, 0, 0]}>
          <CaixaArredondada args={[0.45, 0.045, 1.45]} />
          <meshStandardMaterial color="#facc15" roughness={0.35} />
        </mesh>
        {[-0.14, 0.14].map((x) => (
          <mesh key={x} position={[x, 0.06, -0.55]}>
            <CaixaArredondada args={[0.09, 0.08, 0.16]} />
            <meshStandardMaterial color="#dc2626" roughness={0.7} />
          </mesh>
        ))}
        {[-0.2, 0.05, 0.35].map((z) => (
          <mesh key={z} position={[0, 0.026, z]}>
            <CaixaArredondada args={[0.46, 0.01, 0.05]} />
            <meshStandardMaterial color="#0f172a" roughness={0.9} />
          </mesh>
        ))}
      </group>

      {/* Área interativa para clique/toque de seleção */}
      <mesh visible={false} position={[0, 0.15, 0]}>
        <boxGeometry args={[2.5, 0.35, 1.7]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

/**
 * 9. Poste de Rede Elétrica
 *
 * Poste de concreto de média tensão, também usado como ancoragem:
 * - Fuste de concreto cônico com sapata de base e faixas refletivas
 * - Cruzeta com mão-francesa de reforço
 * - 3 isoladores cerâmicos tipo campânula vitrificada
 * - Fiação aérea contínua nos isoladores (sem fio caído, fogo nem faíscas)
 */
export function PosteTabletop() {
  return (
    <group aria-label="Poste de rede elétrica">
      {/* Base / Sapata de fundação do poste no solo */}
      <mesh position={[0, 0.08, 0]}>
        <cylinderGeometry args={[0.26, 0.28, 0.16, 16]} />
        <meshStandardMaterial color="#52525b" roughness={0.95} />
      </mesh>

      {/* Fuste cônico principal de concreto armado */}
      <mesh position={[0, 2.25, 0]} castShadow>
        <cylinderGeometry args={[0.13, 0.22, 4.5, 16]} />
        <meshStandardMaterial color="#73777a" roughness={0.92} />
      </mesh>

      {/* Faixas refletivas de identificação viária amarela no fuste */}
      <mesh position={[0, 1.35, 0]}>
        <cylinderGeometry args={[0.19, 0.19, 0.12, 16]} />
        <meshStandardMaterial
          color="#facc15"
          emissive="#ca8a04"
          emissiveIntensity={0.25}
          roughness={0.5}
        />
      </mesh>

      {/* Cruzeta superior de distribuição de energia */}
      <mesh position={[0, 4.15, 0]}>
        <CaixaArredondada args={[1.7, 0.14, 0.14]} />
        <meshStandardMaterial color="#334155" roughness={0.8} />
      </mesh>

      {/* Mão-francesa de reforço estrutural em V sob a cruzeta */}
      {[-0.45, 0.45].map((x) => (
        <mesh
          key={x}
          position={[x / 2, 3.82, 0]}
          rotation={[0, 0, x > 0 ? -0.55 : 0.55]}
        >
          <CaixaArredondada args={[0.04, 0.72, 0.03]} />
          <meshStandardMaterial color="#64748b" metalness={0.7} roughness={0.4} />
        </mesh>
      ))}

      {/* 3 Isoladores cerâmicos tipo campânula vitrificada */}
      {[-0.65, 0, 0.65].map((x, idx) => (
        <group key={idx} position={[x, 4.28, 0]}>
          <mesh position={[0, 0, 0]}>
            <cylinderGeometry args={[0.035, 0.065, 0.14, 8]} />
            <meshStandardMaterial color="#78350f" roughness={0.3} metalness={0.1} />
          </mesh>
          <mesh position={[0, 0.08, 0]}>
            <cylinderGeometry args={[0.015, 0.015, 0.06, 6]} />
            <meshStandardMaterial color="#94a3b8" metalness={0.8} roughness={0.3} />
          </mesh>
        </group>
      ))}

      {/* Fiação aérea contínua passando pelos isoladores intactos (esquerdo e central) */}
      <mesh position={[-0.65, 4.36, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.014, 0.014, 4.5, 6]} />
        <meshStandardMaterial color="#1e293b" roughness={0.7} />
      </mesh>
      <mesh position={[0, 4.36, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.014, 0.014, 4.5, 6]} />
        <meshStandardMaterial color="#1e293b" roughness={0.7} />
      </mesh>

      {/* Área interativa para clique/toque de seleção */}
      <mesh visible={false} position={[0, 2.25, 0]}>
        <cylinderGeometry args={[0.45, 0.55, 4.6, 8]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

/**
 * 10. Fogo / Princípio de Incêndio (Elemento Tático Independente)
 *
 * Elemento tático para ser posicionado livremente no veículo (capô, motor,
 * habitáculo, sob o assoalho) ou em qualquer ponto da via pelo instrutor:
 * - Chamas volumosas multicamadas com línguas de fogo dançantes
 * - Coluna de fumaça cinza-escura por convecção
 * - Brasas incandescentes ascendentes
 * - Iluminação dinâmica quente
 * - Solo chamuscado quando ativo ou cinzas apagadas quando extinto
 */
export function FogoTabletop({ aparencia }: { aparencia?: AparenciaRisco }) {
  const extinto = aparencia?.extinto ?? false
  return (
    <group aria-label="Fogo ou princípio de incêndio">
      <Fogo3D ativo={!extinto} escala={1.25} />
      <mesh visible={false} position={[0, 0.4, 0]}>
        <boxGeometry args={[1.2, 1.0, 1.2]} />
        <meshBasicMaterial />
      </mesh>
    </group>
  )
}

